/**
 * Jean Paul — Endorsers page controller
 * Handles: search, instrument filter, sort, and whole-card navigation to
 * the endorser's detail page (sections/main-endorser.liquid).
 *
 * The quick-view modal that used to live here was removed 2026-09-02 at
 * Tim's request — every click on an .endorser-card now navigates straight
 * to the detail page instead. It was endorsers-specific dead code once the
 * detail page shipped (2026-09-01); confirmed via grep that no other
 * section/snippet referenced #endorser-modal or its buildModalHTML markup
 * before deleting it.
 */
(function () {
  'use strict';

  // ── Instrument → JP collection URL map ───────────────────────────────────
  var INSTRUMENT_URLS = {
    'alto saxophone':    '/collections/alto-saxophones',
    'alto sax':          '/collections/alto-saxophones',
    'tenor saxophone':   '/collections/tenor-saxophones',
    'tenor sax':         '/collections/tenor-saxophones',
    'soprano saxophone': '/collections/saxophones',
    'soprano sax':       '/collections/saxophones',
    'baritone saxophone':'/collections/saxophones',
    'baritone sax':      '/collections/saxophones',
    'saxophone':         '/collections/saxophones',
    'sax':               '/collections/saxophones',
    'trumpet':           '/collections/trumpets',
    'flugelhorn':        '/collections/flugelhorns',
    'cornet':            '/collections/trumpets',
    'trombone':          '/collections/trombones',
    'flute':             '/collections/flutes',
    'piccolo':           '/collections/flutes',
    'clarinet':          '/collections/clarinets',
    'mouthpiece':        '/collections/mouthpieces'
  };

  /**
   * Fallback only: best JP collection URL guessed from free-text instrument
   * name. Used solely when an instruments_played entry has no resolved
   * product URL (e.g. stale data from before the field became a reference
   * type). As of 2026-09-01 instruments_played is list.variant_reference, so
   * main-endorsers.liquid resolves a real {label, url} pair server-side and
   * this heuristic should rarely fire.
   */
  function getInstrumentUrl(instrument) {
    var lower = (instrument || '').toLowerCase();
    // Try longest-match first so "alto saxophone" wins over "saxophone"
    var keys = Object.keys(INSTRUMENT_URLS).sort(function (a, b) { return b.length - a.length; });
    for (var i = 0; i < keys.length; i++) {
      if (lower.indexOf(keys[i]) !== -1) return INSTRUMENT_URLS[keys[i]];
    }
    return '/collections/all';
  }

  /** Normalize an instruments_played entry (either a {label,url} object from
   *  the resolved reference, or a legacy plain string) to {label, url}. */
  function normalizeInstrument(inst) {
    if (inst && typeof inst === 'object') {
      return { label: inst.label || '', url: inst.url || getInstrumentUrl(inst.label) };
    }
    return { label: inst || '', url: getInstrumentUrl(inst) };
  }

  // ── Parse endorsers data ──────────────────────────────────────────────────
  var dataEl = document.getElementById('endorsers-data');
  var endorsers = [];
  if (dataEl) {
    try { endorsers = JSON.parse(dataEl.textContent); } catch (err) { /* silently skip */ }
  }

  // ── Grid click / keyboard handler ─────────────────────────────────────────
  // Every click anywhere on an .endorser-card navigates to that endorser's
  // detail page — no quick-view modal. The card already contains two real
  // <a href="{{ e_url }}"> anchors (photo, name) pointing at the same detail
  // URL; a raw <a> can't legally wrap them, so instead of one giant outer
  // link the card stays a role="button" wrapper and this delegated handler
  // navigates for every click that ISN'T already on a nested <a> (letting
  // those anchors navigate natively rather than racing this handler).
  var grid = document.getElementById('endorsers-grid');
  if (grid) {
    grid.addEventListener('click', function (e) {
      if (e.target.closest('a')) return; // nested link (photo/name) — let it navigate natively
      var card = e.target.closest('.endorser-card');
      if (card && card.dataset.url) window.location.href = card.dataset.url;
    });
    grid.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        if (e.target.closest('a')) return;
        var card = e.target.closest('.endorser-card');
        if (card && card.dataset.url) {
          e.preventDefault();
          window.location.href = card.dataset.url;
        }
      }
    });
  }

  // ── Search, filter, sort controls ────────────────────────────────────────
  var searchInput  = document.getElementById('endorsers-search');
  var filterSelect = document.getElementById('endorsers-filter');
  var sortSelect   = document.getElementById('endorsers-sort');
  var noResults    = document.getElementById('endorsers-no-results');
  var countEl      = document.getElementById('endorsers-count');

  // Populate instrument filter from endorsers data
  function populateFilter() {
    if (!filterSelect) return;
    var seen = {};
    var opts = [];
    endorsers.forEach(function (e) {
      (e.instruments_played || []).map(normalizeInstrument).forEach(function (inst) {
        if (!inst.label) return;
        var key = inst.label.toLowerCase();
        if (!seen[key]) { seen[key] = true; opts.push(inst.label); }
      });
    });
    opts.sort(function (a, b) { return a.toLowerCase().localeCompare(b.toLowerCase()); });
    opts.forEach(function (inst) {
      var opt = document.createElement('option');
      opt.value = inst.toLowerCase();
      opt.textContent = inst;
      filterSelect.appendChild(opt);
    });
  }

  function getCards() {
    return grid ? Array.prototype.slice.call(grid.querySelectorAll('.endorser-card')) : [];
  }

  function applyFilters() {
    var query     = searchInput  ? searchInput.value.toLowerCase().trim()  : '';
    var filterVal = filterSelect ? filterSelect.value.toLowerCase()        : '';
    var sortVal   = sortSelect   ? sortSelect.value                        : 'name-asc';

    var cards = getCards();
    var visible = [];

    cards.forEach(function (card) {
      var name     = (card.dataset.name        || '').toLowerCase();
      var insts    = (card.dataset.instruments || '').toLowerCase();
      var location = (card.dataset.location    || '').toLowerCase();

      var matchSearch = !query || name.indexOf(query) !== -1 || insts.indexOf(query) !== -1 || location.indexOf(query) !== -1;
      var matchFilter = !filterVal || insts.indexOf(filterVal) !== -1;

      if (matchSearch && matchFilter) {
        card.style.display = '';
        visible.push(card);
      } else {
        card.style.display = 'none';
      }
    });

    // Sort visible cards
    visible.sort(function (a, b) {
      var na = a.dataset.name || '';
      var nb = b.dataset.name || '';
      var ia = a.dataset.instruments || '';
      var ib = b.dataset.instruments || '';
      if (sortVal === 'name-desc')      return nb.localeCompare(na);
      if (sortVal === 'instrument-asc') return ia.localeCompare(ib) || na.localeCompare(nb);
      return na.localeCompare(nb); // 'name-asc' default
    });
    visible.forEach(function (card) { grid.appendChild(card); });

    // Count / empty state
    if (countEl) countEl.textContent = visible.length + (visible.length === 1 ? ' endorser' : ' endorsers');
    if (noResults) {
      noResults.textContent = visible.length === 0 ? 'No endorsers match your search. Try a different term.' : '';
      noResults.style.display = visible.length === 0 ? 'block' : 'none';
    }
  }

  if (searchInput)  searchInput.addEventListener('input',  applyFilters);
  if (filterSelect) filterSelect.addEventListener('change', applyFilters);
  if (sortSelect)   sortSelect.addEventListener('change',  applyFilters);

  // ── Init ─────────────────────────────────────────────────────────────────
  populateFilter();
  applyFilters(); // sets initial count
}());
